import { useState, useEffect } from "react";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Lock, Save, Upload, Loader2, CalendarDays, Eye, EyeOff, Power } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Switch } from "../components/ui/switch";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (token) {
      fetchAdminData();
    }
  }, [token]);

  const fetchAdminData = async () => {
    try {
      const [roomsRes, bookingsRes, contentRes] = await Promise.all([
        axios.get(`${API}/rooms`),
        axios.get(`${API}/admin/bookings`, { headers: authHeaders }),
        axios.get(`${API}/content`),
      ]);
      setRooms(roomsRes.data);
      setBookings(bookingsRes.data);
      setContent(contentRes.data);
      setIsLoggedIn(true);
    } catch (err) {
      if (err.response?.status === 401) {
        setToken("");
        localStorage.removeItem("admin_token");
        setIsLoggedIn(false);
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setToken(res.data.token);
      localStorage.setItem("admin_token", res.data.token);
      toast.success("Logged in successfully");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const updateRoom = async (roomId, data) => {
    try {
      await axios.put(`${API}/admin/rooms/${roomId}`, data, { headers: authHeaders });
      toast.success("Room updated");
      fetchAdminData();
    } catch {
      toast.error("Failed to update room");
    }
  };

  const updateContent = async (sectionId, data) => {
    try {
      await axios.put(`${API}/admin/content/${sectionId}`, { data }, { headers: authHeaders });
      toast.success("Content updated");
      fetchAdminData();
    } catch {
      toast.error("Failed to update content");
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/upload`, formData, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      toast.success("Image uploaded");
      return imageUrl;
    } catch {
      toast.error("Upload failed");
      return null;
    }
  };

  if (!isLoggedIn) {
    return (
      <div data-testid="admin-login-page">
        <section className="bg-slate-900 py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Panel</h1>
          </div>
        </section>
        <section className="py-24 bg-stone-50">
          <div className="max-w-sm mx-auto px-4">
            <div className="bg-white border border-stone-100 p-8">
              <div className="flex items-center justify-center w-14 h-14 bg-amber-50 mx-auto mb-6">
                <Lock className="w-6 h-6 text-amber-600" />
              </div>
              <h2 className="text-lg font-bold text-center text-slate-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Login</h2>
              <form onSubmit={handleLogin}>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="rounded-none border-stone-200 focus-visible:ring-amber-600 h-11 pr-10"
                    data-testid="admin-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-none h-11"
                  data-testid="admin-login-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Login
                </Button>
              </form>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <section className="bg-slate-900 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Dashboard</h1>
          <Button
            variant="outline"
            onClick={() => { setToken(""); localStorage.removeItem("admin_token"); setIsLoggedIn(false); }}
            className="text-white border-slate-600 hover:bg-slate-800 text-xs rounded-none"
            data-testid="admin-logout-btn"
          >
            Logout
          </Button>
        </div>
      </section>

      <section className="py-8 md:py-12 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="rooms">
            <TabsList className="bg-white border border-stone-100 rounded-none mb-8">
              <TabsTrigger value="rooms" className="rounded-none text-xs uppercase tracking-wider data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="admin-tab-rooms">Rooms</TabsTrigger>
              <TabsTrigger value="content" className="rounded-none text-xs uppercase tracking-wider data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="admin-tab-content">Content</TabsTrigger>
              <TabsTrigger value="bookings" className="rounded-none text-xs uppercase tracking-wider data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
            </TabsList>

            {/* Rooms Tab */}
            <TabsContent value="rooms">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {rooms.map(room => (
                  <RoomEditor key={room.id} room={room} onSave={(data) => updateRoom(room.id, data)} onUpload={uploadImage} />
                ))}
              </div>
            </TabsContent>

            {/* Content Tab */}
            <TabsContent value="content">
              <div className="space-y-6">
                <ContentEditor
                  title="Hero Section"
                  section={content.hero || {}}
                  fields={["headline", "subtext", "image"]}
                  onSave={(data) => updateContent("hero", data)}
                  onUpload={uploadImage}
                />
                <ContentEditor
                  title="About Section"
                  section={content.about || {}}
                  fields={["title", "text"]}
                  onSave={(data) => updateContent("about", data)}
                />
                <ContentEditor
                  title="Restaurant"
                  section={content.restaurant || {}}
                  fields={["title", "text", "hours"]}
                  onSave={(data) => updateContent("restaurant", data)}
                />
                <QrCodeEditor
                  currentQr={content.qr_code || ""}
                  onSave={(url) => updateContent("qr_code", url)}
                  onUpload={uploadImage}
                />
              </div>
            </TabsContent>

            {/* Bookings Tab */}
            <TabsContent value="bookings">
              <div className="bg-white border border-stone-100 overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-bookings-table">
                  <thead className="bg-stone-50 border-b border-stone-100">
                    <tr>
                      <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Name</th>
                      <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Phone</th>
                      <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Room</th>
                      <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Dates</th>
                      <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b, i) => (
                      <tr key={i} className="border-b border-stone-50">
                        <td className="p-3 font-semibold text-slate-800">{b.customer_name}</td>
                        <td className="p-3 text-slate-600">{b.phone}</td>
                        <td className="p-3 text-slate-600">{b.room_type}</td>
                        <td className="p-3 text-slate-600">
                          <div className="flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" />
                            {b.check_in} to {b.check_out}
                          </div>
                        </td>
                        <td className="p-3 font-bold text-amber-600">{'\u20B9'}{b.total_amount?.toLocaleString()}</td>
                      </tr>
                    ))}
                    {bookings.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-slate-400">No bookings yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}

function RoomEditor({ room, onSave, onUpload }) {
  const [data, setData] = useState({ ...room, available: room.available !== false });
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(data);
    setSaving(false);
  };

  const handleToggleAvailability = async (checked) => {
    setToggling(true);
    setData(d => ({ ...d, available: checked }));
    await onSave({ ...data, available: checked });
    setToggling(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await onUpload(file);
    if (url) setData(d => ({ ...d, image: url }));
  };

  return (
    <div className={`bg-white border p-5 ${data.available !== false ? 'border-stone-100' : 'border-red-200 bg-red-50/30'}`}>
      {/* Availability Toggle - Top Bar */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-stone-100">
        <div className="flex items-center gap-2">
          <Power className={`w-4 h-4 ${data.available !== false ? 'text-green-600' : 'text-red-500'}`} />
          <span className={`text-xs font-bold uppercase tracking-wider ${data.available !== false ? 'text-green-700' : 'text-red-600'}`}>
            {data.available !== false ? 'Available' : 'Room Full'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400">{data.available !== false ? 'ON' : 'OFF'}</span>
          <Switch
            checked={data.available !== false}
            onCheckedChange={handleToggleAvailability}
            disabled={toggling}
            data-testid={`room-toggle-${room.id}`}
          />
        </div>
      </div>

      <div className="flex gap-4 mb-4">
        <div className="w-24 h-24 bg-stone-100 overflow-hidden shrink-0">
          <img src={data.image} alt={data.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{room.name}</h3>
          <label className="inline-flex items-center gap-1.5 text-xs text-amber-600 font-semibold cursor-pointer hover:text-amber-700">
            <Upload className="w-3 h-3" /> Change Image
            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          </label>
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Room Name</Label>
          <Input value={data.name} onChange={e => setData(d => ({ ...d, name: e.target.value }))} className="mt-1 rounded-none border-stone-200 h-9 text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Price (per night)</Label>
            <Input type="number" value={data.price} onChange={e => setData(d => ({ ...d, price: parseInt(e.target.value) || 0 }))} className="mt-1 rounded-none border-stone-200 h-9 text-sm" />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Capacity</Label>
            <Input type="number" value={data.capacity} onChange={e => setData(d => ({ ...d, capacity: parseInt(e.target.value) || 2 }))} className="mt-1 rounded-none border-stone-200 h-9 text-sm" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Image URL</Label>
          <Input value={data.image} onChange={e => setData(d => ({ ...d, image: e.target.value }))} className="mt-1 rounded-none border-stone-200 h-9 text-sm" />
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold uppercase tracking-wider rounded-none h-8 px-4">
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
}

function ContentEditor({ title, section, fields, onSave, onUpload }) {
  const [data, setData] = useState({ ...section });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setData({ ...section }); }, [section]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(data);
    setSaving(false);
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file || !onUpload) return;
    const url = await onUpload(file);
    if (url) setData(d => ({ ...d, [field]: url }));
  };

  return (
    <div className="bg-white border border-stone-100 p-6">
      <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{title}</h3>
      <div className="space-y-3">
        {fields.map(field => (
          <div key={field}>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{field}</Label>
            {field === "text" ? (
              <textarea
                value={data[field] || ""}
                onChange={e => setData(d => ({ ...d, [field]: e.target.value }))}
                className="mt-1 w-full rounded-none border border-stone-200 p-2 text-sm min-h-[80px] focus:ring-1 focus:ring-amber-600 focus:outline-none"
              />
            ) : field === "image" ? (
              <div className="flex gap-2 mt-1">
                <Input value={data[field] || ""} onChange={e => setData(d => ({ ...d, [field]: e.target.value }))} className="rounded-none border-stone-200 h-9 text-sm flex-1" />
                {onUpload && (
                  <label className="inline-flex items-center gap-1 text-xs bg-stone-100 px-3 cursor-pointer hover:bg-stone-200 transition-colors duration-200">
                    <Upload className="w-3 h-3" /> Upload
                    <input type="file" accept="image/*" onChange={e => handleImageUpload(e, field)} className="hidden" />
                  </label>
                )}
              </div>
            ) : (
              <Input value={data[field] || ""} onChange={e => setData(d => ({ ...d, [field]: e.target.value }))} className="mt-1 rounded-none border-stone-200 h-9 text-sm" />
            )}
          </div>
        ))}
        <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold uppercase tracking-wider rounded-none h-8 px-4">
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
}


function QrCodeEditor({ currentQr, onSave, onUpload }) {
  const [qrUrl, setQrUrl] = useState(currentQr);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setQrUrl(currentQr); }, [currentQr]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(qrUrl);
    setSaving(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await onUpload(file);
    if (url) setQrUrl(url);
  };

  return (
    <div className="bg-white border border-stone-100 p-6" data-testid="admin-qr-editor">
      <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Payment QR Code</h3>
      <div className="flex flex-col sm:flex-row gap-6">
        <div className="shrink-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Current QR Code</p>
          <div className="w-40 h-40 border-2 border-stone-100 bg-white p-2">
            {qrUrl ? (
              <img src={qrUrl} alt="Payment QR Code" className="w-full h-full object-contain" data-testid="admin-qr-preview" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">No QR set</div>
            )}
          </div>
        </div>
        <div className="flex-1 space-y-3">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">QR Code Image URL</Label>
            <Input
              value={qrUrl || ""}
              onChange={e => setQrUrl(e.target.value)}
              placeholder="Paste image URL here"
              className="mt-1 rounded-none border-stone-200 h-9 text-sm"
              data-testid="admin-qr-url-input"
            />
          </div>
          <div>
            <label className="inline-flex items-center gap-1.5 text-xs bg-stone-100 px-4 py-2 cursor-pointer hover:bg-stone-200 transition-colors duration-200 font-semibold text-slate-600">
              <Upload className="w-3.5 h-3.5" /> Upload New QR Image
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="admin-qr-upload-input" />
            </label>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold uppercase tracking-wider rounded-none h-8 px-4"
            data-testid="admin-qr-save-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            Save QR Code
          </Button>
        </div>
      </div>
    </div>
  );
}
